import React from 'react';
import { shape, number } from 'prop-types';
import classnames from 'classnames';

import { usePromoCart } from '../../../contexts/PromoCartProvider';
import { usePromoModel } from '../../../contexts/PromoModelProvider';
import { formatPrice, getProgressBarSize, getProgressBarColor } from '../../../utils/promo-presentation-utils';

import './PromoSummaryTier.scss';

export const PromoSummaryTier = ({ rewardTier }) => {
  const { rewardTiers } = usePromoModel();
  const {
    isPromoAchieved, currentPromoTier, progressMade, promoCartQuantity, promoCartValue
  } = usePromoCart();

  const isCurrentTierZero = currentPromoTier === 0;
  const isCurrentPromoTier = rewardTier.tier === currentPromoTier;
  const amountToQualify = formatPrice(rewardTier.minPurchaseAmount - promoCartValue);
  const quantityToQualify = rewardTier.minPurchaseQuantity
    ? (rewardTier.minPurchaseQuantity - promoCartQuantity)
    : 0;
  const isPromoInProgress = isCurrentTierZero || progressMade;

  const promoSummaryLockClasses = classnames({
    'promo-summary-tier__messaging-badge--closed':
      ((!progressMade && isCurrentPromoTier) || (rewardTier.tier > currentPromoTier)),
    'promo-summary-tier__messaging-badge--open':
      (progressMade && isCurrentPromoTier),
    'promo-summary-tier__messaging-badge--checkmark':
      rewardTier.tier < currentPromoTier
      || isPromoAchieved,
  });

  const promoSummarySuccessClass = classnames({
    'u__text--success': isCurrentPromoTier
  });

  const promoSummaryUnlockClasses = classnames('promo-summary-tier__unlock-messaging', {
    'promo-summary-tier__unlock-messaging--with-savings': !isCurrentTierZero,
    'promo-summary-tier__unlock-messaging--last': (currentPromoTier === rewardTiers.length - 1)
  });

  const progressHeight = getProgressBarSize({
    rewardTier,
    currentPromoTier,
    progressMade
  });

  const progressColor = getProgressBarColor({
    rewardTier,
    rewardTiers,
    currentPromoTier,
    isPromoAchieved
  });

  const {
    minPurchaseAmount, minPurchaseQuantity, rewardAmountPerOrder, rewardPercent, fixedPriceTotal
  } = rewardTier;

  let orderPercentAmount = '';
  if (rewardPercent && !isCurrentTierZero) {
    if (isPromoAchieved) {
      orderPercentAmount = formatPrice(promoCartValue * (rewardTiers[currentPromoTier].rewardPercent / 100));
    } else {
      orderPercentAmount = formatPrice(promoCartValue * (rewardTiers[currentPromoTier - 1].rewardPercent / 100));
    }
  }

  let orderRewardAmount = '';
  if (rewardAmountPerOrder && !isCurrentTierZero) {
    if (isPromoAchieved) {
      orderRewardAmount = formatPrice(rewardTiers[currentPromoTier].rewardAmountPerOrder);
    } else {
      orderRewardAmount = formatPrice(rewardTiers[currentPromoTier - 1].rewardAmountPerOrder);
    }
  }

  const containerHeight = 100 / rewardTiers.length;

  return (
    <div
      className="promo-summary-tier"
      data-testid="promo-summary-tier"
      style={{ height: `${containerHeight}%` }}
    >
      <div className="promo-summary-tier__progress">
        {rewardTier.tier === 0 && (
          <span
            className="promo-summary-tier__progress-start"
            data-testid="promo-summary-tier__progress-start"
            style={{ backgroundColor: (!isCurrentTierZero || progressMade) ? progressColor : '#CCC' }}
          />
        )}
        <span
          className="promo-summary-tier__progress-segment-transition"
          data-testid={`promo-summary-tier__progress-segment-transition-${rewardTier.tier}`}
          style={{
            height: progressHeight,
            backgroundColor: progressColor,
          }}
        />
      </div>
      <div className="promo-summary-tier__messaging">
        <span className={promoSummaryLockClasses} />
        <div className="promo-summary-tier__messaging-condition">
          <div className="promo-summary-tier__messaging-saving">
            {!!minPurchaseQuantity && (<span>Buy {minPurchaseQuantity},{!fixedPriceTotal && <> Save</>}</span>)}
            {!!minPurchaseAmount && (<span>Spend ${minPurchaseAmount}, Save</span>)}
            {!!rewardPercent && (
              <span className="promo-summary-tier__messaging-saving-amount">
                {rewardPercent}% Off
              </span>
            )}
            {!!rewardAmountPerOrder && (
              <span className="promo-summary-tier__messaging-saving-amount">
                {formatPrice(rewardAmountPerOrder)} Off
              </span>
            )}
            {!!fixedPriceTotal && (
              <span className="promo-summary-tier__messaging-saving-amount">
                Pay {formatPrice(fixedPriceTotal, true)}
              </span>
            )}
          </div>
          {((!isPromoAchieved && isCurrentPromoTier)
            || (isPromoAchieved && rewardTier.tier === rewardTiers.length - 1)) && (
            <span className={promoSummaryUnlockClasses}>
              {!isPromoAchieved ? (
                <span>{minPurchaseAmount ? 'Spend ' : 'Add '}
                  <span className={promoSummarySuccessClass}>
                    {quantityToQualify
                      ? `${!quantityToQualify ? 0 : quantityToQualify}
                        ${!isPromoInProgress && isCurrentTierZero ? 'items' : 'more items'}`
                      : `${!amountToQualify ? 0 : amountToQualify}
                        ${!isPromoInProgress && isCurrentTierZero ? '' : 'more'}`}
                  </span> to{isPromoInProgress || !isCurrentTierZero ? <br /> : ''} unlock
                  {isPromoInProgress || !isCurrentTierZero ? '' : <br />} the next tier
                </span>
              ) : (
                <>
                  <span className="u__text--success">Success!</span>
                  <span>You&apos;ve reached the <br /> top savings tier</span>
                </>
              )}
              {((!isCurrentTierZero || isPromoAchieved) && !fixedPriceTotal) && (
                <span className="promo-summary-tier__cart-messaging">
                  You<span className="promo-summary-tier__cart-messaging__large-screen-only">&apos;re</span>
                  <span
                    className="u__text--success"
                    data-testid="promo-summary-cart-savings"
                  >
                    <span className="promo-summary-tier__cart-messaging__large-screen-only">&nbsp;saving&nbsp;</span>
                    <span className="promo-summary-tier__cart-messaging__small-screen-only">&nbsp;save&nbsp;</span>
                    {orderRewardAmount}
                    {orderPercentAmount}
                  </span>
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

PromoSummaryTier.displayName = 'PromoSummaryTier';

PromoSummaryTier.propTypes = {
  rewardTier: shape({
    tier: number,
    rewardPercent: number,
    rewardAmountPerOrder: number,
  }).isRequired,
};
